/**
 * @graphql cc
 */
import React, { Fragment, useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { ErrorDisplay, isStatusCodeError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';

import { ReadTimeComponent } from './ReadTimeComponent';
import { ReadTimeSmartsQuery } from './ReadTimeSmartsQuery.graphql';
import { OriginalReadTime } from './OriginalReadTime';
import { getAdf, getWordCount } from './readTimeUtils';

export const SmartsReadTime = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
}: {
	contentId: string;
	isAbbreviatedReadTime?: boolean;
	isDetailsPanel?: boolean;
	showWordCount?: boolean;
}) => {
	const { data, loading, error } = useQuery(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded Query
		ReadTimeSmartsQuery,
		{
			variables: { id: contentId },
			errorPolicy: 'all',
			fetchPolicy: 'cache-first',
		},
	);

	const readTime = data?.getSmartContentFeature?.readTime;

	const { wordCount } = useMemo(() => {
		if (loading || error || !contentId) {
			return { wordCount: null };
		}
		const adf = getAdf(data);
		if (!adf) {
			return { wordCount: 0 };
		}
		return {
			wordCount: getWordCount(adf),
		};
	}, [loading, error, data, contentId]);

	if (loading) {
		return null;
	}

	if (readTime == undefined || error) {
		if (isStatusCodeError(error, '403') || error?.message.includes('403')) {
			markErrorAsHandled(error);
			return null;
		}

		return (
			<Fragment>
				<OriginalReadTime contentId={contentId} />
				{error && <ErrorDisplay error={error} />}
			</Fragment>
		);
	}

	return (
		<ReadTimeComponent
			readTime={readTime}
			pageId={contentId}
			isAbbreviatedReadTime={isAbbreviatedReadTime}
			isDetailsPanel={isDetailsPanel}
			showWordCount={showWordCount}
			wordCount={wordCount}
		/>
	);
};
